import React, { useState } from 'react';
import { Card, Button, Upload, message, Typography, Row, Col, Alert, Spin, Modal } from 'antd';
import { UploadOutlined, DownloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { getSystemBackup, restoreSystemBackup } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text, Paragraph } = Typography;

const SystemBackupRestore = () => {
    const { canEditModule, canViewField } = usePermissions();
    const canViewBackup = canViewField('system', 'backup');
    const canViewRestore = canViewField('system', 'restore');
    const [loading, setLoading] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const handleDownloadBackup = async () => {
        setLoading(true);
        try {
            const response = await getSystemBackup();

            // Create a blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Set filename
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'system_backup.json';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch.length === 2)
                    fileName = fileNameMatch[1];
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            message.success('Backup downloaded successfully');
        } catch (error) {
            message.error('Failed to download backup');
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (file) => {
        // Confirm before restore
        Modal.confirm({
            title: 'Are you sure you want to restore?',
            content: 'This will overwrite existing data. Make sure you have a backup before proceeding.',
            okText: 'Yes, Restore',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setRestoring(true);
                try {
                    await restoreSystemBackup(file);
                    message.success('System restored successfully!');
                } catch (error) {
                    message.error('Restoration failed: ' + (error.response?.data?.message || error.message));
                } finally {
                    setRestoring(false);
                }
            }
        });
        return false; // Prevent auto upload
    };

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}><SafetyCertificateOutlined /> System Backup & Restore</Title>
            <Paragraph>
                Manage your system's data integrity with full backup and restore capabilities.
                This operation affects the <strong>MongoDB</strong> database.
            </Paragraph>

            <Alert
                type="warning"
                message="Caution: Global System Action"
                description="Restoring data will replace all current data in the system with the backup file's content. Ensure you have a current backup before restoring."
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Row gutter={24}>
                {canViewBackup && (
                <Col span={12}>
                    <Card title="Export / Backup" bordered={false} style={{ height: '100%' }}>
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <DownloadOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: 16 }} />
                            <Paragraph>
                                Download a complete JSON snapshot of the system.
                                Includes Customers, Bills, Users, Schemes, Permissions, etc.
                            </Paragraph>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                size="large"
                                onClick={handleDownloadBackup}
                                loading={loading}
                            >
                                Download Backup
                            </Button>
                        </div>
                    </Card>
                </Col>
                )}
                {canViewRestore && (
                <Col span={12}>
                    <Card title="Import / Restore" bordered={false} style={{ height: '100%' }}>
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <UploadOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: 16 }} />
                            <Paragraph>
                                Restore the system from a previously generated backup file.
                            </Paragraph>
                            <Upload
                                beforeUpload={handleRestore}
                                showUploadList={false}
                                accept=".json"
                                disabled={!canEditModule('system')}
                            >
                                <Button
                                    danger
                                    size="large"
                                    icon={<UploadOutlined />}
                                    loading={restoring}
                                    disabled={!canEditModule('system')}
                                >
                                    Select Backup File & Restore
                                </Button>
                            </Upload>
                        </div>
                    </Card>
                </Col>
                )}
            </Row>
        </div>
    );
};

export default SystemBackupRestore;
